import React from 'react';
import { BarChart2, Target } from 'lucide-react';
import type { TestInfo } from '@conceptwaves/shared-components';

interface TestResultsChartProps {
  tests: TestInfo[];
  studentName: string;
}

const TestResultsChart: React.FC<TestResultsChartProps> = ({ tests, studentName }) => {
  return (
    <div className="test-results-chart">
      <h4 className="chart-title">
        <BarChart2 size={18} className="icon-blue" />
        Detailed Test Performance
      </h4>

      {tests.map((test, index) => {
        const percentage = ((test.totalScored / test.totalMaxMarks) * 100);
        const isPositive = percentage >= 50;

        return (
          <div key={index} className="test-result-item">
            {/* Test Header */}
            <div className="test-item-header">
              <div className="test-info">
                <h5 className="test-name">{test.testName}</h5>
                <div className="test-meta">
                  <span className="test-category">{test.testCategoryName}</span>
                  <span className="test-score">
                    Score: {test.totalScored} / {test.totalMaxMarks}
                  </span>
                </div>
              </div>
              <div className="test-percentage">
                <div className={`percentage-value ${isPositive ? 'positive' : 'negative'}`}>
                  {percentage.toFixed(1)}%
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="progress-container">
              <div className="progress-bar">
                <div
                  className={`progress-fill ${isPositive ? 'positive' : 'negative'}`}
                  style={{ width: `${Math.max(0, Math.min(100, percentage))}%` }}
                />
              </div>
            </div>

            {/* Sections Breakdown — show only leaf sections. The materialized
                sections array flattens the hierarchy and includes the container
                root (e.g. the "Test" section that just re-states the whole test
                total), which would otherwise render as a duplicate tile. */}
            {(() => {
              const parentIds = new Set(
                (test.sections ?? [])
                  .map((s) => s.parentSectionId)
                  .filter((id): id is number => id !== null && id !== undefined)
              );
              const leafSections = (test.sections ?? []).filter(
                (s) => !parentIds.has(s.sectionId)
              );
              if (leafSections.length === 0) return null;
              return (
              <div className="sections-breakdown">
                <div className="sections-title">
                  <Target size={14} />
                  Section-wise Performance
                </div>
                <div className="sections-grid">
                  {leafSections.map((section, sIdx) => {
                    const sectionPercentage = ((section.scored / section.maxMarks) * 100);
                    const sectionColor = sectionPercentage >= 50 ? 'positive' : 'negative';

                    return (
                      <div key={sIdx} className="section-item">
                        <div className="section-info">
                          <div className="section-name">{section.sectionName}</div>
                          <div className="section-keyword">{section.keywordName}</div>
                        </div>
                        <div className="section-score">
                          <div className={`section-percentage ${sectionColor}`}>
                            {sectionPercentage.toFixed(0)}%
                          </div>
                          <div className="section-marks">
                            {section.scored}/{section.maxMarks}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              );
            })()}
          </div>
        );
      })}
    </div>
  );
};

export default TestResultsChart;
